/**
 * Which admin screens are per-property, and how the choice travels between them.
 *
 * Two bugs lived here before this module existed, both invisible with a single
 * home and both worse with every home added:
 *
 *   - The property switcher is rendered by AdminLayout, so it appeared on screens
 *     whose data is not per-property. Changing it rewrote the URL and changed
 *     nothing, which reads as a broken control.
 *   - Sidebar links were bare paths, so `?property=` was dropped on every click.
 *     You could select one home, navigate, and be reading another's data under
 *     the heading you thought you had chosen.
 */

/**
 * Admin paths whose data belongs to the SITE, not to one home.
 *
 * Blog, backlinks, link opportunities and outreach are properties of the domain:
 * one article, one inbound link, one outreach thread. Users are staff accounts,
 * not guests of a particular house.
 */
export const SITE_WIDE_ADMIN_PATHS = [
  "/admin/blog",
  "/admin/backlinks",
  "/admin/link-opportunities",
  "/admin/outreach",
  "/admin/users",
] as const;

/** True when the screen at `path` is not scoped to a single property. */
export function isSiteWideAdminPath(path: string): boolean {
  const clean = path.split("?")[0].replace(/\/+$/, "") || "/";
  return SITE_WIDE_ADMIN_PATHS.some((p) => clean === p || clean.startsWith(`${p}/`));
}

/** Read the selected property from a location search string. */
export function selectedPropertyParam(search: string): string | null {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  return params.get("property");
}

/**
 * Append the selected property to a link, preserving anything already on it.
 *
 * Carried onto site-wide screens too, which ignore it: a detour through Blog
 * should not silently discard the home you were working on.
 */
export function withPropertyParam(href: string, property: string | null): string {
  if (!property) return href;
  const [path, existing] = href.split("?");
  const params = new URLSearchParams(existing || "");
  params.set("property", property);
  return `${path}?${params.toString()}`;
}
